import { type ReactNode } from 'react';
import {
  PersistQueryClientProvider,
  type PersistQueryClientOptions,
} from '@tanstack/react-query-persist-client';

import { Toaster } from 'sonner';

import { UserSync } from '@/modules/auth/components/user-sync';

import { AuthProvider } from '@/modules/auth/providers/auth.provider';
import { UserProvider } from '@/modules/auth/providers/user.provider';
import { CookiesProvider } from 'react-cookie';
import { GeolocationProvider } from '@/modules/location/providers/geolocation.provider';
import { AddressesProvider } from '@/modules/location/providers/addresses.provider';
import { ServiceProvider } from '@/shared/providers/service.provider';
import { queryClient } from '@/shared/lib/query-client';
import { localStoragePersister } from '@/shared/lib/persister';
import { UserAddressDrawerProvider } from '@/modules/location/components/address-drawer';
import { AnalyticsProvider } from '@/modules/analytics/providers/analytics.provider';

import { Query } from '@tanstack/react-query';

type ProviderProps = {
  children: ReactNode;
};

const COOKIE_OPTIONS = {
  path: '/',
  sameSite: 'lax' as const,
  secure: import.meta.env.MODE === 'production',
  expires: new Date(Date.now() + 1000 * 60 * 60 * 24 * 30), // 30 dias
  httpOnly: false,
} as const;

const PERSIST_OPTIONS: PersistQueryClientOptions = {
  queryClient,
  persister: localStoragePersister,
  maxAge: 1000 * 60 * 60 * 24, // 24h
  buster: 'app-v1',
  dehydrateOptions: {
    shouldDehydrateQuery: (query: Query) => {
      return (
        query?.state?.status === 'success' &&
        (query?.options?.meta?.persist === true || query?.meta?.persist === true)
      );
    },
  },
} as const;

export function Provider({ children }: ProviderProps) {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={PERSIST_OPTIONS}>
      <CookiesProvider defaultSetOptions={COOKIE_OPTIONS}>
        <AuthProvider>
          <UserProvider>
            <ServiceProvider>
              <GeolocationProvider>
                <AddressesProvider>
                  <UserAddressDrawerProvider>
                    <UserSync />
                    <AnalyticsProvider>
                      {children}
                    </AnalyticsProvider>
                  </UserAddressDrawerProvider>
                </AddressesProvider>
              </GeolocationProvider>
              <Toaster position="top-center" />
            </ServiceProvider>
          </UserProvider>
        </AuthProvider>
      </CookiesProvider>
    </PersistQueryClientProvider>
  );
}
